import { useMemo } from 'react';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { gql, Unpacked, isNotNullOrUndefined } from '@yi/core';
import { AdyenError } from '@yi/core/src/custom-errors';

import { notifyBugsnag } from '~/src/common/bugsnagClient';
import type { ErrorMessage } from '~/src/common/ErrorMessage';
import { getMembershipStatus } from '~/src/common/membership/getMembershipStatus';
import { nr } from '~/src/common/newRelic';
import { useCurrentUserSubscription } from '~/src/common/subscription';

import { brooklyn } from './brooklyn';
import { getConfig } from '../getConfig';
import { isErrorOrString } from '../typeGuards';

const { Digital, Premium } = gql.typesClient.MembershipTier;
const { MONTH, YEAR } = gql.typesClient.MembershipPlanInterval;

export const ZERO_PRICE = '0.00';

export const skus = {
  basic: 'YI MO BASIC',
  basicTrial: 'YI 1W MO BASIC',
  annual: 'YI YR BASIC',
  annualTrial: 'YI 1W YR BASIC',
  premium: 'YI YR PREMIUM',
  premiumMonthly: 'YI MO PREMIUM',
  annualWinback129: 'YI YR BASIC WB1',
  // annualWinback99: 'YI YR BASIC WB2',
  basic3Month: 'YI 3MO MO BASIC',
  basic3MonthCancelDeflect: 'YI 3MO MO CD',
} as const;

type SKUKey = keyof typeof skus;
export type SKU = (typeof skus)[keyof typeof skus];

const plansInfo = [
  {
    sku: skus.basic,
    tier: Digital,
    interval: MONTH,
    ratePlanId: getConfig('plans')?.[skus.basic],
  },
  {
    sku: skus.basicTrial,
    tier: Digital,
    interval: MONTH,
    ratePlanId: getConfig('plans')?.[skus.basicTrial],
  },
  {
    sku: skus.annual,
    tier: Digital,
    interval: YEAR,
    ratePlanId: getConfig('plans')?.[skus.annual],
  },
  {
    sku: skus.annualTrial,
    tier: Digital,
    interval: YEAR,
    ratePlanId: getConfig('plans')?.[skus.annualTrial],
  },
  {
    sku: skus.premium,
    tier: Premium,
    interval: YEAR,
    ratePlanId: getConfig('plans')?.[skus.premium],
  },
  {
    sku: skus.premiumMonthly,
    tier: Premium,
    interval: MONTH,
    ratePlanId: getConfig('plans')?.[skus.premiumMonthly],
  },
  {
    sku: skus.annualWinback129,
    tier: Digital,
    interval: YEAR,
    ratePlanId: getConfig('plans')?.[skus.annualWinback129],
  },

  {
    sku: skus.basic3Month,
    tier: Digital,
    interval: MONTH,
    ratePlanId: getConfig('plans')?.[skus.basic3Month],
  },
  {
    sku: skus.basic3MonthCancelDeflect,
    tier: Digital,
    interval: MONTH,
    ratePlanId: getConfig('plans')?.[skus.basic3MonthCancelDeflect],
  },

  // {
  //   sku: skus.annualWinback99,
  //   tier: Digital,
  //   interval: YEAR,
  //   ratePlanId: getConfig('plans')?.[skus.annualWinback99],
  // },
];

interface PlanBase {
  id: string;
  name: string;
  costs: string[];
  currencyIso: string;
  country: string;
  currencySymbol: string;
  sku: string;
}
type PlanInfo = (typeof plansInfo)[0] & { displayPrice: string };
export type TPlan = PlanBase & PlanInfo;
type Plans = { [K in SKUKey]: undefined | TPlan | null };

const makePlan = (plans: PlanBase[], sku: string): TPlan | null => {
  const plan = plans.find(p => p.sku === sku);
  const planInfo = plansInfo.find(p => p.sku === sku);

  // Prefix non-USD currency displays using the ambiguous `$` symbol with currency ISO
  const displayCurrency =
    plan?.currencySymbol === '$' && plan.currencyIso !== 'USD' ? `${plan.currencyIso} ` : '';
  const priceAfterTrial = plan?.costs.filter(c => c !== ZERO_PRICE)?.[0] || ZERO_PRICE;
  const displayPrice = plan ? `${displayCurrency}${plan.currencySymbol}${priceAfterTrial}` : '';
  return plan && planInfo ? Object.assign(plan, planInfo, { displayPrice }) : null;
};

export const fetchPlans = (currencyOverride?: string | 'LTM'): Promise<number> =>
  brooklyn
    .get('/v1/billing/plans', {
      params: {
        skus: plansInfo.map(p => p.sku),
        currencyOverride,
      },
    })
    .then(res => res.data);

// LATAM pricing is considered a currency override ('LTM'). It is discounted USD
const fetchMembershipPlans = async (currencyOverride?: string | 'LTM'): Promise<Plans | null> => {
  try {
    const { data } = await brooklyn.get<PlanBase[]>('/v1/billing/plans', {
      params: {
        skus: plansInfo.map(p => p.sku),
        currencyOverride,
      },
    });

    const plans = Object.entries(skus).reduce<Plans>((acc, [k, sku]) => {
      acc[k as SKUKey] = makePlan(data, sku);
      return acc;
    }, {} as Plans);

    if (Object.values(plans).some(v => !v)) {
      notifyBugsnag(
        `Missing membership plan data: ${JSON.stringify({ ...plans, currencyOverride })}`,
      );
    }
    return plans;
  } catch (e) {
    if (isErrorOrString(e)) {
      nr.noticeError(e);
    } else {
      nr.noticeError(`unexpected error format. Raw error: ${e}`);
    }
    notifyBugsnag(e);
    return null;
  }
};

export const getPlan = (
  plans: TPlan[] | null,
  findBy:
    | { sku: string }
    | { ratePlanId: string }
    | { tier: gql.typesClient.MembershipTier; interval: gql.typesClient.MembershipPlanInterval }
    | { id: string },
) => {
  if (!plans) return null;
  if ('sku' in findBy) return plans.find(p => p.sku === findBy.sku);
  if ('ratePlanId' in findBy) return plans.find(p => p.sku === findBy.ratePlanId);
  if ('tier' in findBy) {
    return plans.find(p => p.tier === findBy.tier && p.interval === findBy.interval);
  }
  if ('id' in findBy) return plans.find(p => p.id === findBy.id);
  return null;
};

// Default to existing country on the user's subscription, otherwise fallback on Brooklyn's IP-based detection
const getCountry = (plans: TPlan[] | null, subscriptionCountry?: string | null) => {
  return subscriptionCountry || plans?.reduce<null | string>((acc, p) => acc || p?.country, null);
};

export type BrooklynPlans = null | Unpacked<ReturnType<typeof fetchMembershipPlans>>;

export const useMembershipPlans = (
  args: {
    subscription?: gql.typesClient.Query_CurrentUserSubscription_CurrentUserSubscription | null;
  } = {},
) => {
  const {
    subscription: queriedSubscription,
    loading: loadingSubscription,
    error: errorSubscription,
    refetch,
    called,
  } = useCurrentUserSubscription({
    skip: !!args.subscription,
  });

  const subscription = args.subscription || queriedSubscription;

  const { isMembershipCanceled } = getMembershipStatus(subscription || null);
  const isLatam = subscription?.price_override === 'latam';

  let currencyOverride = subscription?.currency_code?.toUpperCase() || undefined;
  // LATAM pricing maintained for plan changes, but revoked if subscription canceled
  if (isLatam && !isMembershipCanceled) currencyOverride = 'LTM';

  const {
    data: brooklynPlans,
    error: fetchBrooklynPlansError,
    status: fetchBrooklynPlansStatus,
  } = useReactQuery({
    cacheTime: 5 * 1000, // 5 seconds
    queryKey: ['brooklynPlans', currencyOverride],
    queryFn: async () =>
      fetchMembershipPlans(currencyOverride)
        .then(p => {
          nr.addAction(nr.FETCH_PLANS, {
            success: true,
            currencyOverride,
            subscriptionNumber: subscription?.token || 'none',
          });
          return p;
        })
        .catch(e => {
          nr.noticeError(new AdyenError(e));
          nr.addAction(nr.FETCH_PLANS, {
            success: false,
            currencyOverride,
            subscriptionNumber: subscription?.token || 'none',
          });
        }),
    enabled: !loadingSubscription,
  });

  // NOTE: it's not clear what error format is used on the brooklyn side, so we stringify it here
  // TODO add types for brooklyn errors
  const processedFetchBrooklynPlansError: ErrorMessage | null = useMemo(() => {
    return fetchBrooklynPlansError
      ? {
          message: `fetch brooklyn plan error: ${JSON.stringify(fetchBrooklynPlansError)}`,
        }
      : null;
  }, [fetchBrooklynPlansError]);

  const plansArr =
    (brooklynPlans && Object.values(brooklynPlans).filter(isNotNullOrUndefined)) ?? [];

  return {
    refetch,
    loading: loadingSubscription || fetchBrooklynPlansStatus === 'loading',
    error: errorSubscription || processedFetchBrooklynPlansError,
    plans: brooklynPlans ?? null,
    called,
    getPlan: (findBy: Parameters<typeof getPlan>[1]) => getPlan(plansArr, findBy),
    getCountry: () => getCountry(plansArr, subscription?.country),
  };
};
